import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarkNotificationsRead, useNotifications } from '../lib/queries-s2.js';
import { relativeTime } from '../lib/format.js';
import { Button } from './ui/index.js';

/** Header notifications bell with unread badge and dropdown inbox. */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const query = useNotifications();
  const mark = useMarkNotificationsRead();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = query.data?.unreadCount ?? 0;

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="icon"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="notifications-bell"
      >
        <BellIcon />
        {unread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-error text-[10px] font-semibold text-text-inverse"
            aria-hidden="true"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>
      {open && (
        <div
          role="region"
          aria-label="Notifications"
          className="absolute right-0 z-40 mt-2 w-96 max-w-[90vw] overflow-hidden rounded-xl border border-border bg-surface shadow-(--shadow-card)"
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <p className="text-sm font-medium text-text-primary">Notifications</p>
            {unread > 0 && (
              <Button size="sm" variant="ghost" onClick={() => void mark.all.mutateAsync()}>
                Mark all read
              </Button>
            )}
          </div>
          <ul className="max-h-96 overflow-y-auto">
            {(query.data?.items ?? []).length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-text-muted">
                Nothing here — approvals, assignments and SLA alerts appear as they happen.
              </li>
            )}
            {(query.data?.items ?? []).slice(0, 20).map((n) => (
              <li key={n.id} className={n.readAt ? 'opacity-60' : ''}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-bg-subtle focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  onClick={() => {
                    if (!n.readAt) void mark.one.mutateAsync(n.id);
                    setOpen(false);
                    if (n.linkPath) navigate(n.linkPath);
                  }}
                >
                  <span className="flex items-start justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-text-primary">
                        {n.subject}
                      </span>
                      <span className="block text-xs text-text-secondary">{n.body}</span>
                    </span>
                    <span className="shrink-0 text-xs text-text-muted">
                      {relativeTime(n.createdAt)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BellIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      className="size-4"
      aria-hidden="true"
    >
      <path
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 2.5a5 5 0 00-5 5v2.7l-1.3 2.6a.6.6 0 00.54.87h11.5a.6.6 0 00.55-.87L15 10.2V7.5a5 5 0 00-5-5zM8 15.5a2 2 0 004 0"
      />
    </svg>
  );
}
